<template>
  <div>
    <vx-card v-if="board" class="w-full">
      <div class="flex justify-between items-center">
        <h4 class="mb-8">공정분류표(사내수급) 엑셀 업로드</h4>
        <vs-button v-if="enabledCreateButton" color="secondary" @click="onClickSave">저장</vs-button>
      </div>

      <!-- 기본정보 -->
      <field-select-module v-model="stdInfo" :board="board" :types="['department', 'com_name', 'rank', 'writer_name', 'date']" />
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>파일 업로드</div>
          <div class="flex flex-wrap gap-2">
            <vs-button class="w-full sm:w-auto" color="secondary" type="border" :href="downloadLink">엑셀 템플릿 다운로드</vs-button>
            <vs-button class="w-full sm:w-auto" color="secondary" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
            <input v-show="false" ref="excelInput" type="file" @change="readFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
          </div>
        </div>
      </div>

      <!-- 공정분류표 정보 -->
      <div>
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i> 공정분류표 정보</h5>
          <vs-button color="secondary" type="border" @click="onClickCheckRemove">선택삭제</vs-button>
        </div>
        <div style="max-height: 1070px; overflow-y: auto">
          <table>
            <colgroup>
              <col width="60" />
              <col width="60" />
              <col width="100" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
            </colgroup>
            <thead>
              <th><vs-checkbox :value="0 < excelData.length && excelData.length === checkList.length" @click.native="checkAll" class="check" /></th>
              <th v-for="th in tableHeader" :key="`th-${th}`">{{ th }}</th>
              <th>수정/삭제</th>
            </thead>
            <tbody>
              <tr v-for="(excel, rowIndex) in excelData" :key="`excelData_${rowIndex}`">
                <td><vs-checkbox :vs-value="rowIndex" v-model="checkList" class="check" /></td>
                <td v-for="(columnName, columnIndex) in tableHeader.length" :key="`${rowIndex}_${excel[0]}_${columnIndex}`">
                  <div v-if="editExcelIndex === rowIndex" class="p-1">
                    <fragment v-if="columnIndex === 0">
                      {{ editExcelData.values[columnIndex] }}
                    </fragment>
                    <fragment v-else-if="columnIndex === 2">
                      <field-selector class="w-full text-left" v-model="editExcelData.values[columnIndex].id" @select="(field) => onSelectField(rowIndex, columnIndex, field)" />
                    </fragment>
                    <fragment v-else-if="columnIndex > 0">
                      <ml-input v-model="editExcelData.values[columnIndex]" class="w-full" />
                    </fragment>
                  </div>
                  <div v-else>
                    <p v-if="columnIndex === 2">{{ excel.values[columnIndex].field_name || "-" }}</p>
                    <p v-else>{{ excel.values[columnIndex] || "-" }}</p>
                    <p v-if="excel.errorMSG" class="errMsg">{{ excel.errorMSG[columnIndex] || "" }}</p>
                  </div>
                </td>
                <td>
                  <div class="flex justify-center gap-3">
                    <!-- 저장/닫기 -->
                    <fragment v-if="editExcelIndex === rowIndex">
                      <button @click="onClickFinishExcelData(rowIndex)">
                        <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                      </button>
                      <button @click="onClickCancelExcelData(rowIndex)">
                        <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                      </button>
                    </fragment>
                    <!-- 수정/삭제 -->
                    <fragment v-else>
                      <button @click="onClickEditExcelData(rowIndex)">
                        <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                      </button>
                      <button @click="onClickRemoveExcelData(rowIndex)">
                        <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                      </button>
                    </fragment>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FieldSelector from "@/components/selector/FieldSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import DateSelector from "@/components/selector/DateSelector";
import config from "/public/config";
import moment from "moment";
import HttpRequest from "@/util/HttpRequest";
import ExcelJS from "exceljs";
import RadioGroup from "@/components/radio/RadioGroup.vue";

export default {
  components: {
    RadioGroup,
    FieldSelectModule,
    FieldSelector,
    CompanySelector,
    DateSelector,
  },
  props: {
    board: Object,
  },
  data() {
    return {
      downloadLink: `${config.BASE_API_URL}/storage/app/media/%EA%B3%B5%EC%A0%95%EB%B6%84%EB%A5%98%ED%91%9C_%EC%A7%81%EC%98%81_%EB%8C%80%EB%9F%89%EB%93%B1%EB%A1%9D.xlsx`,
      fields: [],
      stdInfo: {},
      // 엑셀 데이터 관련
      // 데이터구조 :
      //  [
      //    {
      //       values: [1, 'header.year', 'field_name', 'data1', 'data2', ...],
      //       errorMSG: ["", "", "", "", ...]
      //    }
      //  ]
      editExcelIndex: null,
      editExcelData: [],
      excelData: [],
      checkList: [],
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    pageTitle() {
      return this.$store.state.menu.pageTitle;
    },
    tableHeader() {
      return ["No.", "연도", "사업장명", "업체명", "공장명", "대분류", "중분류", "소분류"];
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    __permission_boards() {
      return this.$store.state.member.memberInfo.boards || [];
    },
    permission() {
      if (this.__permission_boards.length > 0) {
        const find = this.__permission_boards.find((board) => board.id === this.boardId);

        if (find) {
          return find.pivot;
        }
      }
      return { c: 0, r: 0, u: 0, d: 0, v: 0 };
    },
    enabledCreateButton() {
      if (this.permission) {
        return this.permission.c;
      }
      return false;
    },
  },
  async mounted() {
    await this.loadFieldListAll();
  },
  methods: {
    // 중복저장을 방지하기 위해 전체 목록을 불러옴
    async loadPostListWithMeta(boardId, searchList) {
      this.loading(true);
      const list = await this._loadDefragmentation({
        boardId,
        projectId: this.projectId,
        page: 1,
        perPage: 50,
        orderTarget: "created_at",
        orderDirection: "desc",
        withMeta: true,
      });
      let allList = list.map((data) => {
        const postContents = data.postContents;
        const header = postContents.header;
        const body = postContents.body;
        const year = header.year ? header.year : new Date().getFullYear();
        const 사업장 = body.사업장 ? { id: body.사업장.id, field_name: body.사업장.field_name } : { id: 0, field_name: "" };
        const 업체명 = body.업체명;
        const 공장명 = body.공장명;
        const 대분류 = body.대분류;
        const 중분류 = body.중분류;
        const 소분류 = body.소분류;

        return { year, 사업장, 업체명, 공장명, 대분류, 중분류, 소분류 };
      });
      allList = allList.filter((data) => {
        for (const search of searchList) {
          const values = search.values;

          // 모든 데이터가 일치하는 경우만 반환
          if (values[1] === data.year && `${values[2].id}` === `${data.사업장.id}` && values[3] === data.업체명 && values[4] === data.공장명 && values[5] === data.대분류 && values[6] === data.중분류 && values[7] === data.소분류) {
            return true;
          }
        }
        return false;
      });
      this.loading(false);

      return allList;
    },
    // 전체 목록을 다운로드 받기 위해 사용
    async _loadDefragmentation(options) {
      const response = await this.$store.dispatch("board/LOAD_POST_LIST", options);
      const list = [];
      list.push(...response.data);
      if (response.current_page !== response.last_page) {
        options.page = response.current_page + 1;
        list.push(...(await this._loadDefragmentation(options)));
      }
      return list;
    },

    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store
          .dispatch("project/LOAD_FIELD_LIST", {
            projectId: this.projectId,
            page,
            perPage: 50,
          })
          .then((response) => {
            const data = response.data;
            this.fields.push(...data);
            if (response.last_page > page) {
              loadFieldList(page + 1);
            } else {
              this.loading(false);
            }
          });
      };
      await loadFieldList(1);
    },

    findFieldByFieldName(fieldName) {
      return this.fields.find((field) => field.field_name === fieldName);
    },

    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    // 엑셀데이터 파싱
    readFile(event) {
      const workbook = new ExcelJS.Workbook();
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const buffer = reader.result;
        workbook.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, rowNumber) => {
              // data form 가공
              const cells = row.values.map((cell) => {
                if (cell.richText) return cell.richText.map((rt) => rt.text).join("");
                if (cell instanceof Date) return moment(cell).format("YYYY-MM-DD");
                return cell;
              });
              this.getDataInSheet(sheet, rowNumber, cells);
            });
          });
          //
          // this.excelData = data;
        });
      };
    },

    // rowNumber: 2부터 시작
    // cells: [empty, col1, col2, col3, ...]
    async getDataInSheet(sheet, rowNumber, cells = []) {
      if (rowNumber > 2) {
        const field = this.findFieldByFieldName(cells[2]);
        const error = [];
        cells[0] = this.excelData.length + 1;
        if (field) {
          cells[2] = {
            id: field.id,
            field_name: field.field_name,
          };
        } else {
          cells[2] = {
            id: 0,
            field_name: cells[2],
          };
          error[2] = "입력된 데이터가 검색되지 않습니다.";
        }
        this.excelData.push({
          values: cells,
          errorMSG: error,
        });
      }
    },

    setMoment(date) {
      let _date = date;
      if (typeof _date === "number") _date = `${_date}`;
      if (typeof _date === "string" && !_date.includes("-")) _date = `${_date.slice(0, 4)}-${_date.slice(4, 6)}-${_date.slice(6, 8)}`;
      return moment(_date).add(1, "days").format("YYYY-MM-DD");
    },
    setPhone(data) {
      let _data = `${data}`;
      if (typeof data === "number" && _data.length === 10 && _data.charAt(0) !== "0") _data = `0${_data}`;
      return _data.replace(/^(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData(index) {
      this.editExcelData = [];
      this.editExcelIndex = null;
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    checkAll() {
      if (0 < this.excelData.length && this.excelData.length === this.checkList.length) this.checkList = [];
      else this.checkList = this.excelData.map((_, i) => i);
    },
    onClickCheckRemove() {
      if (this.checkList.length < 1) return;
      this.excelData = this.excelData.filter((_, i) => !this.checkList.includes(i));
      this.excelData.forEach((_, i) => {
        _.values[0] = i + 1;
      });
      this.checkList = [];
    },
    async onClickSave() {
      // 중복 데이터 제거
      const forms = [
        ...new Set(
          this.excelData.map((ed) => {
            const data = JSON.parse(JSON.stringify(ed.values));
            const form = {
              year: data[1],
              stdInfo: this.stdInfo,
              사업장: data[2],
              업체명: data[3],
              공장명: data[4],
              대분류: data[5],
              중분류: data[6],
              소분류: data[7],
            };
            return JSON.stringify(form);
          })
        ),
      ].map((d) => JSON.parse(d));

      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.customAlert("공정분류표 정보 데이터가 없습니다");
        return;
      }

      // 에러가 있는 필드가 있을 경우,
      if (this.excelData.filter((row) => row.errorMSG.filter((msg) => !!msg).length > 0).length > 0) {
        this.customAlert("공정분류표 정보에 올바르지 않은 데이터가 있습니다");
        return;
      }

      const boardId = 895;
      const searchList = this.excelData;
      const allList = await this.loadPostListWithMeta(boardId, searchList);

      // 중복 제거 후 전체 목록과 비교하여 중복 제거
      const uniqueList2 = forms.filter((data) => {
        return !allList.some(
          (t) =>
            `${t.year}` === `${data.year}` &&
            `${t.사업장.id}` === `${data.사업장.id}` &&
            `${t.업체명}`.trim() === `${data.업체명}`.trim() &&
            `${t.공장명}`.trim() === `${data.공장명}`.trim() &&
            `${t.대분류}`.trim() === `${data.대분류}`.trim() &&
            `${t.중분류}`.trim() === `${data.중분류}`.trim() &&
            `${t.소분류}`.trim() === `${data.소분류}`.trim()
        );
      });

      if (uniqueList2.length > 0) {
        this.writeOnClickSaveMultiple({
          posts: uniqueList2,
          callback: (result, response) => {
            if (result) {
              this.excelData = [];
              this.checkList = [];
              const msg = "저장되었습니다";
              this.customAlert(msg, "공정분류표 엑셀 업로드");
            }
          },
        });
      } else {
        this.$vs.notify({
          title: "저장완료",
          text: "0건 저장되었습니다.",
          color: "success",
          position: "top-right",
        });
        this.excelData = [];
        this.checkList = [];
      }
    },
    writeOnClickSaveMultiple(forms) {
      const boardId = 895;
      this.loading(true);
      const posts = forms.posts.map((form) => {
        const header = {
          date: `${form.year}-01-01`,
          full: `${form.year}-01-01`,
          half: "상반기",
          month: 1,
          quarter: "1",
          year: form.year,
        };
        delete form.year;
        const content = {
          header,
          body: {
            ...form,
          },
        };
        return {
          title: this.pageTitle,
          content: JSON.stringify(content),
          projectId: this.projectId,
          company_member_join: true,
        };
      });

      HttpRequest.put(`/api/v1/board/post/${boardId}/multiple`, { posts })
        .then((response) => {
          if (forms.callback) {
            forms.callback(true, response);
          }

          this.loading(false);
        })
        .catch(({ response }) => {
          if (forms.callback) {
            forms.callback(false, response);
          }
          this.loading(false);
        });
    },
    onSelectField(rowIndex, columnIndex, field) {
      this.editExcelData.values[columnIndex] = {
        id: field.id,
        field_name: field.field_name,
      };
      this.editExcelData.errorMSG[columnIndex] = field.id ? "" : "입력된 데이터가 검색되지 않습니다.";
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>
